/**
 * 웹 위젯 메시지 디바운스 — 손님이 한 생각을 여러 줄로 나눠 보낼 때 조각마다 답하지 않고
 * 조용해질 때까지 모았다가 합쳐서 한 번에 묻는다(네이버 서버 디바운스의 웹판).
 *
 * ## 왜 세 단계인가
 *
 * 처음엔 두 단계였다 — 질문 표지가 있으면 1.2초, 없으면 3.5초. 그런데 3.5초가 모자랐다.
 *
 * 실측(2026-09-03 10:30 양주잼잼, 웹):
 *   10:30:00.250  손님 "6인들어가면"
 *   10:30:01.272  손님 "이불 따로 주시나요?"
 *   10:30:06.319  챗봇 — 6인 **요금** 안내 (신뢰도 45%)
 *   10:30:06.720  챗봇 — 이불 안내 (41%)
 * 한 질문이 둘로 쪼개져 답도 둘로 나갔고, 앞의 답은 묻지도 않은 요금을 안내했다.
 *
 * `6인들어가면` 은 **`~면` 으로 끝나는 미완성 문장**이다. 질문 표지가 없다는 이유로
 * 3.5초만 기다렸는데, 손님이 다음 줄을 치는 데 그보다 조금 더 걸렸다. 끝맺지 않은 말은
 * "아직 할 말이 남았다"는 뚜렷한 신호라, 완결된 평서문보다 더 기다려야 한다.
 *
 * ⚠️ 무작정 늘리면 안 된다. 한 줄만 쓰고 마는 손님은 그 시간만큼 답을 못 받는다.
 * 그래서 **미완성 신호가 있을 때만** 늘린다.
 */

/** 질문 의도가 뚜렷하다 — 바로 답해도 된다. */
export const DEBOUNCE_SHORT_MS = 1200;
/** 완결된 평서문 — 뒤에 한 줄 더 올 수 있으니 조금 기다린다. */
export const DEBOUNCE_LONG_MS = 3500;
/** 말이 끊긴 조각 — 다음 줄이 거의 확실하다. */
export const DEBOUNCE_FRAGMENT_MS = 6500;

/**
 * 질문 의도 표지. 물음표뿐 아니라 종결 어미까지 본다 —
 * 손님은 물음표를 자주 생략한다("주차 몇대 되요").
 */
const QUESTION_RE = /[?？]|나요|까요|ㄹ까|을까|은가요|인가요|되나|돼요|되요|될까|하나요|가능한가|얼마|언제|어디|어떻게|어떤|몇|뭐|무엇|있나요|없나요|주세요|되는지|하는지|알려/;

/**
 * 미완성 신호 — 연결 어미로 끝나면 문장이 아직 안 끝났다.
 *
 * `~면/~고/~는데/~인데/~라서/~니까/~지만/~거나/~며/~다가` 와 `-아서/-어서` 계열
 * (`와서·해서·워서` 같은 모음 축약형 포함 — 테스트가 `비가 와서` 를 놓친 것을 잡았다), 그리고
 * 조사로 끝나는 경우(`~은/는/이/가/을/를/에/에서/으로`)도 뒤가 남았다는 뜻이다.
 * 물음표가 이미 있으면 완결로 본다(질문이 먼저다).
 */
const FRAGMENT_RE = /(면|고|는데|인데|라서|[아어여와워해]서|니까|지만|거나|며|다가|은|는|이|가|을|를|에|에서|으로|로|와|과|랑|하고)$/;

/** 이 조각을 보내기 전에 얼마나 기다릴지(ms). */
export function debounceMsFor(txt: string): number {
  const s = (txt ?? '').trim();
  if (!s) return DEBOUNCE_SHORT_MS;
  // 질문 표지가 있으면 완결로 본다 — 더 기다리면 답만 늦어진다.
  if (QUESTION_RE.test(s)) return DEBOUNCE_SHORT_MS;
  // 문장부호로 끝났으면 손님이 맺은 것이다.
  if (/[.!。！…]$/.test(s)) return DEBOUNCE_LONG_MS;
  if (FRAGMENT_RE.test(s)) return DEBOUNCE_FRAGMENT_MS;
  return DEBOUNCE_LONG_MS;
}
